import React from 'react';
import styles from './Legal.module.css';

const TermsOfService = () => {
  return (
    <div className={styles.legalPage}>
      <div className={styles.container}>
        <h1>Terms of Service</h1>
        <p className={styles.lastUpdated}>Last Updated: March 28, 2026</p>

        <div className={styles.content}>
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By downloading, accessing, or using LeanWeek-AI (the "App"), you agree to be bound by these 
              Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.
            </p>
          </section>

          <section>
            <h2>2. Eligibility</h2>
            <p>
              You must be at least 18 years old to use LeanWeek-AI. By using the App, you represent and 
              warrant that you meet this age requirement and have the legal capacity to enter into a binding contract.
            </p>
          </section>

          <section>
            <h2>3. App Purpose & AI Generation</h2>
            <p>
              LeanWeek-AI is an application designed to provide structured, 7-day fitness plans using artificial 
              intelligence and localized rule-based fallback systems.
            </p>
            <ul>
              <li>The App uses the physical and environmental data you input to generate workout suggestions.</li>
              <li>
                Due to the nature of machine learning and automated generation, we do not guarantee that every 
                generated workout will be perfectly suited to your exact environment. You must use common sense 
                when executing any generated plan.
              </li>
            </ul>
          </section>

          <section>
            <h2>4. No Medical Advice / No Guarantee of Results</h2>
            <p>
              <strong>LeanWeek-AI is not a medical application, and we are not healthcare providers.</strong>
            </p>
            <ul>
              <li>
                <strong>Not Medical Advice:</strong> The workout plans, caloric estimates, and guidance provided 
                by the App are for general informational and educational purposes only. They do not constitute 
                medical advice, diagnosis, or treatment.
              </li>
              <li>
                <strong>Consult a Professional:</strong> Always consult with a qualified healthcare professional 
                before beginning any new diet, exercise, or fitness program.
              </li>
              <li>
                <strong>No Guarantees:</strong> We make no guarantees concerning any specific physical results, 
                fat loss, or health improvements from using the App. Your progress depends entirely on your own 
                effort, consistency, and individual physiology.
              </li>
            </ul>
          </section>

          <section>
            <h2>5. User Responsibilities & Assumption of Risk</h2>
            <p>
              Fitness activities carry an inherent risk of injury. By using LeanWeek-AI, you agree that:
            </p>
            <ul>
              <li>You are voluntarily participating in these activities and assume all risk of injury to yourself.</li>
              <li>You will evaluate your own physical condition and limits before attempting any exercise.</li>
              <li>
                If you experience pain, dizziness, exhaustion, or shortness of breath, you will stop exercising 
                immediately and seek medical attention.
              </li>
              <li>You are responsible for ensuring your exercise environment is safe and your equipment is in good working order.</li>
            </ul>
          </section>

          <section>
            <h2>6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Reverse-engineer, decompile, or attempt to extract the source code or AI logic of the App.</li>
              <li>Use the App in any way that violates applicable laws or regulations.</li>
              <li>Attempt to bypass any paywalls or subscription mechanisms.</li>
            </ul>
          </section>

          <section>
            <h2>7. Subscriptions and Payments</h2>
            <p>
              LeanWeek-AI may offer premium features via auto-renewing subscriptions (e.g., LeanWeek Premium).
            </p>
            <ul>
              <li>
                <strong>Billing:</strong> Payment will be charged to your Apple App Store or Google Play account 
                at confirmation of purchase.
              </li>
              <li>
                <strong>Renewals:</strong> Subscriptions automatically renew unless auto-renew is turned off at 
                least 24 hours before the end of the current period.
              </li>
              <li>
                <strong>Cancellations:</strong> You must manage your subscriptions and cancellations directly 
                through your device’s native app store settings. Deleting the App does not automatically cancel 
                your subscription.
              </li>
            </ul>
          </section>

          <section>
            <h2>8. Intellectual Property</h2>
            <ul>
              <li>
                <strong>Our Property:</strong> All content, design, text, graphics, and interfaces within the App 
                (excluding your personal inputs) are the exclusive property of LeanWeek-AI and are protected by 
                intellectual property laws.
              </li>
              <li>
                <strong>Your Output:</strong> You retain all rights to the "Share Cards" and PDF Recaps you 
                generate and export from the App.
              </li>
            </ul>
          </section>

          <section>
            <h2>9. Disclaimers</h2>
            <p>
              THE APP IS PROVIDED ON AN "AS-IS" AND "AS-AVAILABLE" BASIS. TO THE MAXIMUM EXTENT PERMITTED BY LAW, 
              WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE 
              APP WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY ACCURATE.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL LEANWEEK-AI, ITS FOUNDERS, OR 
              AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR 
              ANY LOSS OF PROFITS OR REVENUES, OR ANY PHYSICAL INJURY ARISING OUT OF YOUR USE OF THE APP. IN NO 
              EVENT SHALL OUR AGGREGATE LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS 
              PRECEDING THE CLAIM, OR $50 USD, WHICHEVER IS GREATER.
            </p>
          </section>

          <section>
            <h2>11. Termination</h2>
            <p>
              We reserve the right to modify, suspend, or terminate the App, or your access to it, at any time 
              for any reason, without notice or liability. You may terminate these Terms at any time by deleting 
              the App from your device.
            </p>
          </section>

          <section>
            <h2>12. Changes to Service and Terms</h2>
            <p>
              LeanWeek-AI is an evolving product. We reserve the right to update these Terms at any time. If we 
              make material changes, we will notify you within the App or by updating the date at the top of this 
              document. Continued use of the App following any updates constitutes your acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2>13. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of Botswana, without regard 
              to its conflict of law provisions. Any legal action or proceeding arising under these Terms will be 
              brought exclusively in the courts located in Gaborone.
            </p>
          </section>

          <section>
            <h2>14. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p><strong>Email:</strong> support@leanweek-ai.com</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;